import React from "react";

/**
 * Stylized miniatures of the real CrewPilot UI (Project workspace, Pictures,
 * Favorites / Ready offline). Pure CSS/SVG, decorative only (aria-hidden by
 * the parent), fictional sample data, no images or media.
 */

export function StarIcon({ filled = false }) {
  return (
    <svg viewBox="0 0 24 24" className="wn-icon" focusable="false">
      <path
        d="m12 3.2 2.7 5.6 6.1.8-4.5 4.2 1.1 6-5.4-3-5.4 3 1.1-6L3.2 9.6l6.1-.8L12 3.2Z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="wn-icon" focusable="false">
      <path
        d="m5 12.5 4.5 4.5L19 7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SignalOffIcon() {
  return (
    <svg viewBox="0 0 24 24" className="wn-icon" focusable="false">
      <path
        d="M2 9a15 15 0 0 1 20 0M5.5 12.6a10 10 0 0 1 13 0M9 16.2a5 5 0 0 1 6 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" className="wn-icon" focusable="false">
      <path
        d="M4 8.5A1.5 1.5 0 0 1 5.5 7H8l1.2-2h5.6L16 7h2.5A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" className="wn-chev" focusable="false">
      <path
        d="m9 6 6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Photo({ tone, className = "" }) {
  return <span className={`wn-ph wn-ph--${tone} ${className}`} />;
}

/** Scene 1 — a fanned stack of the new surfaces. */
export function RedesignArt() {
  return (
    <div className="wn-art wn-art--stack">
      <div className="wn-glass wn-stack__card wn-stack__card--left wn-anim" style={{ "--d": "260ms" }}>
        <div className="wn-skel wn-skel--label" />
        <div className="wn-row-mini"><span>Total Hours</span><b>128.5</b></div>
        <div className="wn-row-mini"><span>Labour</span><b>$9,420</b></div>
        <div className="wn-row-mini"><span>Materials</span><b>$4,115</b></div>
      </div>
      <div className="wn-glass wn-stack__card wn-stack__card--right wn-anim" style={{ "--d": "380ms" }}>
        <div className="wn-photo-grid">
          <Photo tone="steel" />
          <Photo tone="timber" />
          <Photo tone="slate" />
          <Photo tone="dusk" />
        </div>
      </div>
      <div className="wn-glass wn-glass--hero wn-stack__card wn-stack__card--front wn-anim" style={{ "--d": "120ms" }}>
        <div className="wn-client">NORTHSHORE BUILDERS</div>
        <div className="wn-project-line">
          <div>
            <div className="wn-project-name">Maple Street Reno</div>
            <div className="wn-project-meta">PRJ-0142 · Active</div>
          </div>
          <span className="wn-star wn-star--on"><StarIcon filled /></span>
        </div>
        <div className="wn-chip wn-chip--ready"><CheckIcon /> Ready offline</div>
      </div>
    </div>
  );
}

/* Real CrewPilot MobileBottomNav icons (same paths), for recognition. */
const NAV_ICONS = {
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
    </svg>
  ),
  projects: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a1 1 0 0 1 1-1h4l2 2h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7Z" />
    </svg>
  ),
  time: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l3 2" />
      <path d="M9 2h6" />
    </svg>
  ),
  more: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
};

const NAV_TABS = [
  { id: "home", label: "Home" },
  { id: "projects", label: "Projects" },
  { id: "time", label: "Time" },
  { id: "more", label: "More" },
];

/**
 * Scene 2 — the redesigned navigation. The bottom bar mirrors the real
 * MobileBottomNav (Home / Projects / Time / More + Ashley's slot); focus
 * travels Home → Projects → Time → More and settles on Projects while the
 * mini screen above crossfades. A faint desktop window sits behind the phone:
 * same CrewPilot, adapted by device. Decorative, not a tutorial.
 */
export function NavigationArt() {
  return (
    <div className="wn-art wn-art--nav">
      <div className="wn-desk wn-anim" style={{ "--d": "100ms" }}>
        <div className="wn-desk__side">
          <i /><i /><i /><i />
        </div>
        <div className="wn-desk__main">
          <i className="wn-desk__title" />
          <div className="wn-desk__cards"><i /><i /><i /></div>
          <i className="wn-desk__row" /><i className="wn-desk__row" />
        </div>
      </div>

      <div className="wn-phone wn-anim" style={{ "--d": "220ms" }}>
        <div className="wn-phone__screen">
          <div className="wn-screen wn-screen--home">
            <div className="wn-screen__title">Good morning</div>
            <div className="wn-screen__stats"><i /><i /></div>
            <i className="wn-screen__line" /><i className="wn-screen__line wn-screen__line--short" />
          </div>
          <div className="wn-screen wn-screen--projects">
            <div className="wn-screen__title">Projects</div>
            <div className="wn-screen__item wn-screen__item--hot"><b>Maple Street Reno</b><span>★</span></div>
            <div className="wn-screen__item"><b>Harbour Duplex</b></div>
            <div className="wn-screen__item"><b>Elm Avenue Deck</b></div>
          </div>
          <div className="wn-screen wn-screen--time">
            <div className="wn-screen__title">Time</div>
            <div className="wn-screen__clock"><i /></div>
            <div className="wn-screen__cta">Start session</div>
          </div>
          <div className="wn-screen wn-screen--more">
            <div className="wn-screen__title">More</div>
            <div className="wn-screen__grid"><i /><i /><i /><i /><i /><i /></div>
          </div>
        </div>

        <div className="wn-nav">
          <i className="wn-nav__pill" />
          {NAV_TABS.map((tab) => (
            <span key={tab.id} className={`wn-nav__item wn-nav__item--${tab.id}`}>
              {NAV_ICONS[tab.id]}
              <em>{tab.label}</em>
            </span>
          ))}
          <span className="wn-nav__ashley"><i /></span>
        </div>
      </div>
    </div>
  );
}

/** Scene 2 — the Project workspace: organization plus the job's costing. */
export function WorkspaceArt() {
  return (
    <div className="wn-art">
      <div className="wn-glass wn-panel">
        <div className="wn-client wn-anim" style={{ "--d": "80ms" }}>NORTHSHORE BUILDERS</div>
        <div className="wn-project-line wn-anim" style={{ "--d": "140ms" }}>
          <div className="wn-project-meta">PRJ-0142 · Active</div>
          <span className="wn-star"><StarIcon /></span>
        </div>

        <div className="wn-tags wn-opt wn-anim" style={{ "--d": "220ms" }}>
          <span className="wn-tag">Kitchen</span>
          <span className="wn-tag">Basement</span>
          <span className="wn-tag">Exterior</span>
        </div>

        <div className="wn-tiles wn-anim" style={{ "--d": "300ms" }}>
          <div className="wn-navrow wn-navrow--lit">
            <div><b>Pictures</b><span>24 photos</span></div>
          </div>
          <div className="wn-navrow">
            <div><b>Activity</b><span>9 sessions</span></div>
          </div>
        </div>

        <div className="wn-section wn-opt wn-anim" style={{ "--d": "380ms" }}>Job Costs</div>
        <div className="wn-row-mini wn-anim" style={{ "--d": "440ms" }}><span>Labour</span><b>$1,102.50</b></div>
        <div className="wn-row-mini wn-anim" style={{ "--d": "500ms" }}><span>Materials</span><b>$640.00</b></div>
        <div className="wn-row-mini wn-anim" style={{ "--d": "560ms" }}><span>Other Costs</span><b>$125.00</b></div>
        <div className="wn-total wn-anim" style={{ "--d": "700ms" }}>
          <span>Total Project Cost</span>
          <b>$1,867.50</b>
        </div>
      </div>
    </div>
  );
}

/** Billing — an invoice the way the job actually shows Paid and Balance. */
export function BillingArt() {
  return (
    <div className="wn-art">
      <div className="wn-glass wn-glass--hero wn-panel">
        <div className="wn-client wn-anim" style={{ "--d": "80ms" }}>NORTHSHORE BUILDERS</div>
        <div className="wn-project-line wn-anim" style={{ "--d": "150ms" }}>
          <div>
            <div className="wn-project-name">INV-1042</div>
            <div className="wn-project-meta">Bathroom · Partially Paid</div>
          </div>
        </div>
        <div className="wn-row-mini wn-anim" style={{ "--d": "240ms" }}><span>Paid</span><b>$1,200.00</b></div>
        <div className="wn-total wn-anim" style={{ "--d": "320ms" }}>
          <span>Balance</span>
          <b>$2,190.00</b>
        </div>
      </div>
    </div>
  );
}

/** Pictures organized by Project and Location. */
export function PicturesArt() {
  return (
    <div className="wn-art">
      <div className="wn-glass wn-panel">
        <div className="wn-section wn-section--first wn-anim" style={{ "--d": "80ms" }}>Overall Project</div>
        <div className="wn-photo-row wn-anim" style={{ "--d": "160ms" }}>
          <Photo tone="steel" />
          <Photo tone="slate" />
          <Photo tone="dusk" />
        </div>

        <div className="wn-section wn-anim" style={{ "--d": "260ms" }}>Locations</div>
        <div className="wn-place wn-anim" style={{ "--d": "320ms" }}>Kitchen</div>
        <div className="wn-photo-row wn-photo-row--kitchen">
          <Photo tone="timber" className="wn-anim" />
          <Photo tone="steel" className="wn-anim wn-ph--late" />
          <span className="wn-ph wn-ph--new">
            <i className="wn-flash" />
          </span>
        </div>
        <div className="wn-place wn-opt wn-anim" style={{ "--d": "460ms" }}>Basement</div>
        <div className="wn-photo-row wn-opt wn-anim" style={{ "--d": "520ms" }}>
          <Photo tone="slate" />
          <Photo tone="dusk" />
        </div>
      </div>
      <span className="wn-shutter" aria-hidden="true"><CameraIcon /></span>
    </div>
  );
}

/**
 * Scene 5 — Planning: the Timeline | Calendar toggle, a month with a few dated
 * days and a selected-day agenda. Restrained and decorative; it only shows
 * what Planning really has (target dates and task deadlines).
 */
const PLAN_DAYS = [
  [null, null, null, 1, 2, 3, 4],
  [5, 6, 7, 8, 9, 10, 11],
  [12, 13, 14, 15, 16, 17, 18],
];
const PLAN_MARKS = { 2: 1, 9: 2, 10: 1, 16: 1 };

export function PlanningArt() {
  return (
    <div className="wn-art">
      <div className="wn-glass wn-glass--hero wn-panel wn-panel--plan">
        <div className="wn-plan-toggle wn-anim" style={{ "--d": "80ms" }}>
          <span>Timeline</span>
          <span className="is-on">Calendar</span>
        </div>
        <div className="wn-plan-month wn-anim" style={{ "--d": "160ms" }}>October 2026</div>
        <div className="wn-plan-grid wn-anim" style={{ "--d": "220ms" }}>
          {PLAN_DAYS.flat().map((day, index) => (
            <span
              key={index}
              className={`wn-plan-day${day === 7 ? " is-today" : ""}${day === 9 ? " is-picked" : ""}`}
            >
              {day ? <b>{day}</b> : null}
              {day && PLAN_MARKS[day] ? (
                <i className={PLAN_MARKS[day] > 1 ? "wn-plan-count" : "wn-plan-dot"}>
                  {PLAN_MARKS[day] > 1 ? PLAN_MARKS[day] : null}
                </i>
              ) : null}
            </span>
          ))}
        </div>
        <div className="wn-plan-agenda wn-anim" style={{ "--d": "340ms" }}>
          <div className="wn-plan-item">
            <span className="wn-plan-tag"><i className="wn-plan-diamond" /> Project target</span>
            <span className="wn-plan-name">Maple Street Reno</span>
          </div>
          <div className="wn-plan-item wn-opt">
            <span className="wn-plan-tag"><i className="wn-plan-circle" /> Task due</span>
            <span className="wn-plan-name">Install shower tile</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Scene 4 — Favorite a Project → Preparing → Ready offline, no signal. */
export function OfflineArt() {
  return (
    <div className="wn-art">
      <div className="wn-offline-pill wn-anim" style={{ "--d": "60ms" }}>
        <SignalOffIcon /> No signal
      </div>
      <div className="wn-glass wn-glass--hero wn-panel wn-panel--fav">
        <div className="wn-client wn-anim" style={{ "--d": "140ms" }}>NORTHSHORE BUILDERS</div>
        <div className="wn-project-line wn-anim" style={{ "--d": "200ms" }}>
          <div>
            <div className="wn-project-name">Maple Street Reno</div>
            <div className="wn-project-meta">PRJ-0142 · Active</div>
          </div>
          <span className="wn-star wn-star--tap">
            <span className="wn-star__off"><StarIcon /></span>
            <span className="wn-star__on"><StarIcon filled /></span>
            <i className="wn-star__ring" />
          </span>
        </div>

        <div className="wn-status wn-anim" style={{ "--d": "260ms" }}>
          <span className="wn-status__prep"><i className="wn-spin" /> Preparing offline…</span>
          <span className="wn-status__ready"><CheckIcon /> Ready offline</span>
        </div>

        <div className="wn-package wn-opt wn-anim" style={{ "--d": "340ms" }}>
          <span className="wn-package__item" style={{ "--i": 0 }}><CheckIcon /> Project details</span>
          <span className="wn-package__item" style={{ "--i": 1 }}><CheckIcon /> Locations</span>
          <span className="wn-package__item" style={{ "--i": 2 }}><CheckIcon /> Picture thumbnails</span>
        </div>
      </div>
    </div>
  );
}

export const WHATS_NEW_ART = Object.freeze({
  redesign: RedesignArt,
  navigation: NavigationArt,
  workspace: WorkspaceArt,
  pictures: PicturesArt,
  billing: BillingArt,
  planning: PlanningArt,
  offline: OfflineArt,
});
